import { useState, useEffect, useCallback } from "react";
import { apiClient } from "~/services/api-client";

interface TimeseriesDataPoint {
  datetime: string;
  price: number;
  percent_move: number;
}

interface UseSecuritiesPerformanceProps {
  figis?: string[];
  timeframe?: "1M" | "3M" | "6M" | "1Y"; // Default to 1M
  autoFetch?: boolean;
}

interface UseSecuritiesPerformanceReturn {
  performanceData: Map<string, number | null>;
  timeseriesData: Map<string, TimeseriesDataPoint[]>;
  isLoading: boolean;
  error: string | null;
  fetchPerformance: () => Promise<void>;
}

// Calculate performance percentage from timeseries data
function calculatePerformance(
  timeseries: TimeseriesDataPoint[],
  daysBack: number,
): number | null {
  if (!timeseries || timeseries.length === 0) {
    return null;
  }

  // Filter out entries with zero prices and sort by date descending (most recent first)
  const sorted = [...timeseries]
    .filter((point) => point.price > 0)
    .sort(
      (a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime(),
    );

  // Need at least 2 valid data points to calculate performance
  if (sorted.length < 2) {
    return null;
  }

  // Get most recent non-zero price
  const currentPrice = sorted[0]?.price;

  // Get price from ~daysBack ago (approximately)
  // We'll try to get close to the target date, accounting for weekends/holidays
  const targetIndex = Math.min(daysBack, sorted.length - 1);
  const pastPrice = sorted[targetIndex]?.price;

  if (!currentPrice || !pastPrice || pastPrice === 0) {
    return null;
  }

  // Calculate percentage change
  const performancePercent = ((currentPrice - pastPrice) / pastPrice) * 100;
  return performancePercent;
}

// Get number of trading days for timeframe
function getDaysForTimeframe(timeframe: string): number {
  switch (timeframe) {
    case "1M":
      return 21; // ~1 month of trading days
    case "3M":
      return 63; // ~3 months of trading days
    case "6M":
      return 126; // ~6 months of trading days
    case "1Y":
      return 252; // ~1 year of trading days
    default:
      return 21;
  }
}

// Shared cache for performance data and timeseries
const performanceCache = new Map<
  string,
  {
    performanceData: Map<string, number | null>;
    timeseriesData: Map<string, TimeseriesDataPoint[]>;
    timestamp: number;
  }
>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const CHUNK_SIZE = 50;

/**
 * Hook to fetch performance data for multiple securities.
 *
 * Sends one bulk request per chunk of up to CHUNK_SIZE FIGIs
 * (comma-separated `?figi=A,B,C,...`) rather than one request per FIGI.
 */
export function useSecuritiesPerformance({
  figis = [],
  timeframe = "1M",
  autoFetch = false,
}: UseSecuritiesPerformanceProps = {}): UseSecuritiesPerformanceReturn {
  const [performanceData, setPerformanceData] = useState<
    Map<string, number | null>
  >(new Map());
  const [timeseriesData, setTimeseriesData] = useState<
    Map<string, TimeseriesDataPoint[]>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch one chunk (up to CHUNK_SIZE FIGIs) in a single HTTP call.
  // Returns a map keyed by FIGI. Missing entries in the response (e.g.
  // unknown FIGI) are returned with null performance + empty timeseries
  // so the caller can render a placeholder rather than retry silently.
  const fetchChunkPerformance = useCallback(
    async (
      figisChunk: string[],
      daysBack: number,
    ): Promise<
      Map<
        string,
        { performance: number | null; timeseries: TimeseriesDataPoint[] }
      >
    > => {
      const out = new Map<
        string,
        { performance: number | null; timeseries: TimeseriesDataPoint[] }
      >();
      for (const figi of figisChunk) {
        out.set(figi, { performance: null, timeseries: [] });
      }
      if (figisChunk.length === 0) return out;

      try {
        const params = new URLSearchParams({
          figi: figisChunk.join(","),
          interval: "MONTH",
        });
        const response = await apiClient.request({
          method: "GET",
          url: `/api/v1/timeseries/securities?${params}`,
        });

        if (response.error) {
          console.warn(
            `Failed to fetch timeseries chunk (${figisChunk.length} FIGIs):`,
            response.error,
          );
          return out;
        }

        if (!Array.isArray(response.data)) return out;

        for (const entry of response.data as Array<{
          figi?: string;
          timeseries?: TimeseriesDataPoint[];
        }>) {
          if (!entry.figi || !Array.isArray(entry.timeseries)) continue;
          out.set(entry.figi, {
            performance: calculatePerformance(entry.timeseries, daysBack),
            timeseries: entry.timeseries,
          });
        }
      } catch (err) {
        console.error(
          `Error fetching timeseries chunk (${figisChunk.length} FIGIs):`,
          err,
        );
      }
      return out;
    },
    [],
  );

  const fetchPerformance = useCallback(async () => {
    if (!figis || figis.length === 0) {
      setError("No FIGIs provided");
      return;
    }

    // Create cache key from figis and timeframe
    const sortedFigis = [...figis].sort();
    const cacheKey = `${sortedFigis.join(",")}_${timeframe}`;

    // Check cache first
    const cached = performanceCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setPerformanceData(cached.performanceData);
      setTimeseriesData(cached.timeseriesData);
      return;
    }

    setIsLoading(true);
    setError(null);

    const perfResults = new Map<string, number | null>();
    const tsResults = new Map<string, TimeseriesDataPoint[]>();
    const daysBack = getDaysForTimeframe(timeframe);

    try {
      for (let i = 0; i < figis.length; i += CHUNK_SIZE) {
        const chunk = figis.slice(i, i + CHUNK_SIZE);
        const chunkResults = await fetchChunkPerformance(chunk, daysBack);
        for (const [figi, { performance, timeseries }] of chunkResults) {
          perfResults.set(figi, performance);
          tsResults.set(figi, timeseries);
        }

        // Progressive UI update so the first chunk paints while later
        // chunks are still in flight on huge portfolios. Pacing between
        // chunks is handled inside `fetchChunkPerformance` via
        // `acquireRateLimitSlot`, not here.
        setPerformanceData(new Map(perfResults));
        setTimeseriesData(new Map(tsResults));
      }

      // Cache the complete results (both performance and timeseries)
      performanceCache.set(cacheKey, {
        performanceData: perfResults,
        timeseriesData: tsResults,
        timestamp: Date.now(),
      });

      setPerformanceData(perfResults);
      setTimeseriesData(tsResults);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred";
      console.error("Securities performance fetch error:", err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [figis, timeframe, fetchChunkPerformance]);

  // Clear data when FIGIs change
  useEffect(() => {
    setPerformanceData(new Map());
    setTimeseriesData(new Map());
    setError(null);
  }, [figis]);

  useEffect(() => {
    if (autoFetch && figis && figis.length > 0) {
      fetchPerformance();
    }
  }, [figis, autoFetch, fetchPerformance]);

  return {
    performanceData,
    timeseriesData,
    isLoading,
    error,
    fetchPerformance,
  };
}
